import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import { Scrollbars } from 'react-custom-scrollbars';

import Progress from 'components/common/Progress';
import Link from 'components/common/Link';
import IconPlus from 'components/icons/IconPlus';
import IconNoSearchResults from 'components/icons/IconNoSearchResults';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';
import Button from 'components/common/Button';

import InventorySearchResultsItem from './InventorySearchResultsItem';

const styles = theme => ({
  root: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    overflow: 'hidden',
    zIndex: 10,
    [theme.breakpoints.down('sm')]: {
      overflow: 'auto',
      height: 'auto',
      maxHeight: 'calc(100vh - 265px)',
      maxHeight: 'calc(100dvh - 265px)',
    },
  },
  list: {
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      minWidth: 1000,
      width: '100%',
    },
  },
  noSearchResults: {
    height: 304,
    minHeight: 304,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      height: 'calc(100vh - 290px)',
      minHeight: 'calc(100vh - 290px)',
    },
  },
  iconNoSearchResults: {
    width: 102,
    height: 100,
    marginBottom: 25,
  },
  iconPlus: {
    width: 14,
    height: 14,
    marginRight: 10,
  },
  listFooter: {
    display: 'flex',
    position: 'relative',
    padding: '22px 30px',
    borderTop: `1px solid ${theme.palette.primary.A400}`,
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      position: 'sticky',
      bottom: 0,
      zIndex: 10,
      backgroundColor: theme.palette.common.white,
    },
    [theme.breakpoints.down('xs')]: {
      padding: '16px 8px',
    },  
  },
  imagePlaceholder: {
    width: 80,
  },
  listHeader: {
    padding: '16px 25px 16px 24px',
    backgroundColor: theme.palette.primary.A200,
    [theme.breakpoints.down('sm')]: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      minWidth: 1000,
      width: '100%',
      padding: 8,
      boxSizing: 'border-box',
    },
  },
  buttonClose: {
    marginTop: -8,
    marginBottom: -8,
  },
  iconArrowHeadBottom: {
    transform: 'rotate(180deg)',
    width: 14,
    height: 14,
    marginRight: 10,
  },
});

class InventorySearchResults extends Component {
  constructor(props) {
    super(props);

    this.state = {
      autoHeightMax: 400,
    };

    this.onScrollbarsUpdate = this.onScrollbarsUpdate.bind(this);
  }

  componentDidMount() {
    this.onScrollbarsUpdate();
    window.addEventListener('resize', this.onScrollbarsUpdate);
  }

  componentDidUpdate(prevProps) {
    const { inventory, selectedIndex } = this.props;
    const { autoHeightMax } = this.state;

    if (inventory !== prevProps.inventory) {
      this.onScrollbarsUpdate();
    }

    if (this.selectedResultsItemRef) {
      const scrollTop = (this.selectedResultsItemRef.offsetTop
        + this.selectedResultsItemRef.getBoundingClientRect().height)
        - autoHeightMax;
      if (scrollTop >= 0) {
        this.scrollbarsRef.scrollTop(scrollTop);
      }
    }
    if (selectedIndex === 0
      && prevProps.selectedIndex !== 0
      && this.scrollbarsRef) {
      this.scrollbarsRef.scrollTop(0);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onScrollbarsUpdate);
  }

  onScrollbarsUpdate() {
    const offsetTop = this.containerRef
      ? this.containerRef.getBoundingClientRect().top
      : 0;
    const listFooterHeight = this.listFooterRef && this.props.inventory
      ? this.listFooterRef.getBoundingClientRect().height
      : 0;
    const listHeaderHeight = this.props.inventory ? 48 : 0;
    const gutter = 20;
    let autoHeightMax = window.innerHeight - listFooterHeight - offsetTop - gutter - listHeaderHeight;
    if (autoHeightMax < 140) {
      autoHeightMax = 140;
    }
    this.setState({ autoHeightMax });
  }

  render() {
    const {
      inventory,
      onInventoryCreateButtonClick,
      onScrollFetchRequest,
      onResultsItemClick,
      onClick,
      loading,
      selectedIndex,
      onCloseClick,
      width,
      classes,
    } = this.props;
    const { autoHeightMax } = this.state;
    return (
      <Paper
        className={classes.root}
        onMouseDown={onClick}
      >
        <div
          ref={(containerRef) => {
            this.containerRef = containerRef;
          }}
        >
          {inventory.length > 0 ? (
            <Fragment>
              <div
                className={classes.listHeader}
              >
                <Grid
                  container
                  justify="flex-start"
                  alignItems="flex-start"
                  spacing={8}
                  wrap="nowrap"
                >
                  <Grid item>
                    <div className={classes.imagePlaceholder} />
                  </Grid>
                  <Grid
                    item
                    xs={2}
                  >
                    <Typography variant="caption">
                      Product Name
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Serial / Model
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Vendor
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Category
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Condition
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Quantity
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Retail Price
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Facility
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  >
                    <Typography variant="caption">
                      Warehouse
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                  />
                </Grid>
              </div>
              <List disablePadding className={classes.list}>
                {(width !== 'xs' && width !== 'sm') ? (
                  <Scrollbars
                    autoHeight
                    autoHeightMin={0}
                    autoHeightMax={autoHeightMax}
                    thumbMinSize={30}
                    universal
                    onScrollFrame={(event) => {
                      if (event.top > 0.8) {
                        onScrollFetchRequest();
                      }
                    }}
                    ref={(scrollbarsRef) => {
                      this.scrollbarsRef = scrollbarsRef;
                    }}
                  >     
                    {inventory.map((item, index) => (
                      <InventorySearchResultsItem
                        key={item.ID}
                        onAddButtonClick={onResultsItemClick}
                        {...item}
                        {...index === selectedIndex ? {
                          selected: true,
                          rootRef: (selectedResultsItemRef) => {
                            this.selectedResultsItemRef = selectedResultsItemRef;
                          },
                        } : {}}
                      />
                    ))}
                  </Scrollbars>
                ) : (
                  <Fragment>  
                    {inventory.map((item, index) => (
                      <InventorySearchResultsItem
                        key={item.ID}
                        onAddButtonClick={onResultsItemClick}
                        {...item}
                        {...index === selectedIndex ? {
                          selected: true,
                          rootRef: (selectedResultsItemRef) => {
                            this.selectedResultsItemRef = selectedResultsItemRef;
                          },
                        } : {}}
                      />
                    ))}
                  </Fragment>
                )}
              </List>
            </Fragment>
          ) : (
            <Fragment>
              <div
                className={classes.noSearchResults}
              >
                {loading > 0 ? (
                  <Progress />
                ) : (
                  <Fragment>
                    <IconNoSearchResults className={classes.iconNoSearchResults} />
                    <Typography variant="subheading" align="center">
                      Unfortunately, there is no such product
                    </Typography>
                  </Fragment>
                )}
              </div>
            </Fragment>
          )}  
          <div
            className={classes.listFooter}
            ref={(listFooterRef) => {
              this.listFooterRef = listFooterRef;
            }}
          >
            {onInventoryCreateButtonClick &&
              <Link
                color="primary"
                bold
                size="lg"
                onMouseDown={() => {
                  onCloseClick();
                  onInventoryCreateButtonClick();
                }}
              >
                <IconPlus className={classes.iconPlus} />
                Create New Product
              </Link>
            }
            <Button
              variant="raised"
              className={classes.buttonClose}
              onClick={onCloseClick}
            >
              <IconArrowHeadBottom className={classes.iconArrowHeadBottom} />
              Hide
            </Button>
          </div>
        </div>
      </Paper>
    );
  }
}

InventorySearchResults.propTypes = {
  inventory: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  onInventoryCreateButtonClick: PropTypes.func,
  onScrollFetchRequest: PropTypes.func,
  onResultsItemClick: PropTypes.func,
  onClick: PropTypes.func,
  selectedIndex: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  onCloseClick: PropTypes.func,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    list: PropTypes.string.isRequired,
    noSearchResults: PropTypes.string.isRequired,
    iconNoSearchResults: PropTypes.string.isRequired,
    iconPlus: PropTypes.string.isRequired,
    listFooter: PropTypes.string.isRequired,
  }).isRequired,
};

InventorySearchResults.defaultProps = {
  onInventoryCreateButtonClick: null,
  onScrollFetchRequest: () => {},
  onResultsItemClick: () => {},
  onClick: () => {},
  onCloseClick: () => {},
};

export default withStyles(styles)(withWidth()(InventorySearchResults));



// WEBPACK FOOTER //
// ./src/components/inventory/InventorySearch/InventorySearchResults.js